<i18n>
{
  "en": {
    "productColName": "Product",
    "noRows": "No data is available with the current constraints, please either complete the filters or select others."
  },
  "es": {
    "productColName": "Producto",
    "noRows": "No hay datos disponibles para los filtros seleccionados, por favor, termina de seleccionar los filtros o cambia la selección de los mismos."
  }
}
</i18n>

<template>
  <component
    v-if="isLoaded"
    :is="vGrid"
    :source="rows"
    :columns="columns"
    theme="material"
    resize="true"
    @beforeedit="onBeforeEdit"
    @afteredit="onAfterEdit"
    @beforeeditstart="onBeforeEditStart"
    @beforerangeedit="onBeforeRangeEdit"
    range="true"
    class="price-table"
    @headerclick="onHeaderClick"
    @beforeautofill="onBeforeAutoFill"
  />
  <div v-else-if="showNoRowsMessage" class="grey--text text-center">{{ $t("noRows") }}</div>

</template>

<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import { MapActions, MapGetters } from '@core/helpers/decorators';
import { notValidNumber } from '@core/helpers/formValidators';
import { Prop, Watch } from 'vue-property-decorator';
import DummyVGrid from '@dynameat/components/DummyVGrid.vue';
import cloneDeep from 'lodash/cloneDeep';
import { FrontPriceList } from '../models';
import PriceCell from './PriceCell.vue';
import ProductCell from '../../../components/ProductCell.vue';

@Component
@MapActions(['undoPush', 'undoPop', 'undoClear'])
@MapGetters(['serviceList', 'restrictionIntervalList', 'groupList', 'menuFilterList'])
export default class PriceTable extends Vue {
  @Prop({ type: Object, default: () => ({}) }) frontPrices;

  vGrid = null;

  VGridVueTemplate = (attr) => attr;

  validator = notValidNumber('error');

  today = new Date();

  stylesCache = new Map();

  rows = [];

  columns = [];

  // Extra styles for any regular cell.
  regularCell() {
    return {
      style: {
        'border-top': '#b4b4b4 solid 1px',
        'border-left': '#b4b4b4 solid 1px',
        'font-size': '15px'
      },
    };
  }

  coloredServiceCell(colorHex) {
    return {
      style: {
        'border-top': '#b4b4b4 solid 1px',
        'border-left': '#b4b4b4 solid 1px',
        'background-color': `${colorHex}`,
        'font-size': '15px'
      },
    };
  }

  // Extra styles for the cells that can not be edited.
  disabledCell() {
    return {
      style: {
        'background-color': '#f4f4f4',
        'border-top': '#b4b4b4 solid 1px',
        'border-left': '#b4b4b4 solid 1px',
        'font-style': 'italic',
        color: '#747474'
      }
    };
  }

  lastRowRegular() {
    return {
      style: {
        'border-bottom': '#b4b4b4 solid 1px',
      }
    };
  }

  lastColumnRegular() {
    return {
      style: {
        'border-right': '#b4b4b4 solid 1px',
      }
    };
  }

  touchedCell() {
    return {
      style: {
        'background-color': 'rgb(254 246 214 / 58%)',
        'border-top': '#b4b4b4 solid 1px',
        'border-left': '#b4b4b4 solid 1px',
      },
      class: 'warning-cell'
    };
  }

  errorCell() {
    return {
      style: {
        'background-color': '#ff5e5e',
        'border-top': '#b4b4b4 solid 1px',
        'border-left': '#b4b4b4 solid 1px',
      },
      class: 'error-cell'
    };
  }

  // This properties will apply to the "2021-01-01" cells.
  mainColumnProps() {
    return {
      size: 300,
      columnProperties: ({ name }) => ({
        style: {
          'font-style': this.isEnabledCell(name, this.today) ? 'regular' : 'italic',
          'text-align': 'center',
          'background-color': this.$vuetify.theme.currentTheme.primary,
          color: 'white',
          'border-top': '#b4b4b4 solid 1px',
          'border-left': '#b4b4b4 solid 1px',
          'font-size': '18px'
        }
      }),
    };
  }

  get isLoaded() {
    return this.vGrid && this.frontPrices instanceof FrontPriceList && this.table.rows.length > 0 && this.rows.length > 0;
  }

  // First column props, which corresponds to the product.
  get initialColumn() {
    return {
      name: this.$t('productColName'),
      prop: 'productName',
      pin: 'colPinStart',
      sortable: true,
      size: 300,
      cellTemplate: this.firstCellTemplate,
      cellProperties: ({ model }) => this.getStyleForCell()(null, model),
      columnProperties: () => this.getStyleForCell()(null, null),
    };
  }

  // The way of revo grid to dirty-mount a component for the cell.
  get cellTemplate() {
    return this.VGridVueTemplate(PriceCell);
  }

  get firstCellTemplate() {
    return this.VGridVueTemplate(ProductCell);
  }

  // Properties for both any cells except the product ones and for the header cells of "Comidas".
  get childColumnProps() {
    return {
      size: 150,
      cellTemplate: this.cellTemplate,
      cellProperties: ({ prop, model }) => this.getStyleForCell()(prop, model),
      columnProperties: ({ prop }) => this.getStyleForCell()(prop, null, true),
    };
  }

  get table() {
    let table;
    if (this.frontPrices instanceof FrontPriceList) {
      table = this.frontPrices.toTable(this.serviceList, this.mainColumnProps(), this.childColumnProps, this.initialColumn);
    } else {
      table = { columns: [], rows: [] };
    }
    this.rows = table.rows;
    this.columns = table.columns;
    return table;
  }

  get rangeSelector() {
    return this.$el.querySelector('.price-table .rgCol revogr-overlay-selection[slot=content]');
  }

  get showNoRowsMessage() {
    return !this.isLoaded && this.menuFilterList.length;
  }

  mounted() {
    // This is needed in order to properly load the component. Revo-grid does some hacky things
    // to make itself compatible with every framework, and to ensure compatibility, we need to
    // lazy import and use <component>
    import('@revolist/vue-datagrid').then((m) => {
      this.vGrid = m.VGrid;
      this.VGridVueTemplate = m.VGridVueTemplate;
    }).catch(() => {
      this.vGrid = DummyVGrid;
      this.VGridVueTemplate = (attr) => attr;
    });
  }

  getCellColor(prop) {
    const parts = prop.split('_');
    const date = parts[0];
    const serviceGuid = parts[1];
    const foundObject = this.frontPrices.groupList.find((group) => group.serviceGuid === serviceGuid && group.date === date);
    if (foundObject) {
      const { codeName } = foundObject;
      const foundGroup = this.groupList.find((group) => group.code_name === codeName);
      if (foundGroup) {
        return foundGroup.color;
      }
    }
    return '#FFFFFF';
  }

  isErrorCell(prop, model) {
    const cellPrice = model[prop];
    const minPrice = model[`${prop}_min`];
    const maxPrice = model[`${prop}_max`];
    return cellPrice < minPrice || cellPrice > maxPrice;
  }

  isTouchedCell(prop, model) {
    const newPrice = model[prop];
    const mpddGuid = model[`${prop}_guid`];
    return this.frontPrices.hasPriceChanged(mpddGuid, newPrice);
  }

  isEnabledCell(prop, today) {
    // The cell is only enabled in the future and in the future services of today.
    const parts = prop.split('_');
    const day = new Date(parts[0]);
    const isFuture = today < day;
    if (isFuture) {
      return true;
    }
    const isToday = today.toDateString() === day.toDateString();
    if (isToday) {
      const serviceFuture = this.serviceList.some((service) => service.isBeforeTime(today)) || !this.restrictionIntervalList?.isBetweenTime(today);
      if (parts.length === 1) {
        return serviceFuture;
      }
      const servicePresent = this.serviceList.find((service) => service.guid === parts[1])?.isBetweenTime(today);
      return serviceFuture || (servicePresent && !this.restrictionIntervalList?.isBetweenTime(today));
    }
    return false;
  }

  isLastRowCell(model) {
    return this.rows.findIndex((row) => row.productGuid === model.productGuid) === this.rows.length - 1;
  }

  isLastColumnCell(prop) {
    const columns = this.columns.filter((col) => col.children).flatMap((col) => col.children.map((child) => child.prop));
    return columns.findIndex((column) => column === prop) === columns.length - 1;
  }

  isLastColumnHeader(prop) {
    const parts = prop.split('_');
    let res = true;
    if (parts.length > 1) {
      res = res && this.isLastColumnCell(prop);
    }
    const header = parts[0];
    const columns = this.columns.map((col) => col.name);
    return res && columns.findIndex((column) => column === header) === columns.length - 1;
  }

  lastPositionCell(prop, model, currentStyle) {
    if (model) {
      if (this.isLastRowCell(model)) {
        const lastRowStyle = this.lastRowRegular();
        currentStyle.style = { ...currentStyle.style, ...lastRowStyle.style };
      }
      if (prop && this.isLastColumnCell(prop)) {
        const lastColumnStyle = this.lastColumnRegular();
        currentStyle.style = { ...currentStyle.style, ...lastColumnStyle.style };
      }
    } else if (prop && this.isLastColumnHeader(prop)) {
      const lastColumnStyle = this.lastColumnRegular();
      currentStyle.style = { ...currentStyle.style, ...lastColumnStyle.style };
    }
    return currentStyle;
  }

  getStyleForCell() {
    // This is a function that returns a function. It is used to bind the parameters to the function,
    // so they are not always accessed from the vue instance.
    return (prop, model, header = false) => {
      const key = `${prop || 'default'}_${model ? model.productGuid : 'default'}`;
      if (!this.stylesCache.has(key)) {
        // The inside function returns the styles of the cell based on the prop.
        let result;
        // If it's the service row of the header
        if (header) {
          result = this.coloredServiceCell(this.getCellColor(prop));
        } else {
          if (prop) {
            if (this.isEnabledCell(prop, this.today)) {
              if (this.isErrorCell(prop, model)) {
                result = this.errorCell();
              } else if (this.isTouchedCell(prop, model)) {
                result = this.touchedCell();
              } else result = this.regularCell();
            } else {
              result = this.disabledCell();
            }
          } else {
            result = this.regularCell();
          }
          result = this.lastPositionCell(prop, model, result);
        }
        this.stylesCache.set(key, result);
      }
      return this.stylesCache.get(key);
    };
  }

  invalidateCellCache(model, prop) {
    let keyToDelete = null;
    // eslint-disable-next-line no-restricted-syntax
    for (const key of this.stylesCache.keys()) {
      const keyElements = key.split('_');
      if (keyElements.length > 2) {
        const columnKey = key.split('_').splice(0, 2).join('_');
        const rowKey = key.split('_')[2];
        if (prop === columnKey && model.productGuid === rowKey) {
          keyToDelete = key;
          break;
        }
      }
    }
    this.stylesCache.delete(keyToDelete);
    this.$refs?.revo?.$el?.refresh();
  }

  onAfterEdit({ detail }) {
    // Send the changes so they are queued.
    const changes = [];
    // Single edit model
    if (detail.model) {
      changes.push(this.getPriceChange(detail.model, detail.prop, detail.val));
      this.invalidateCellCache(detail.model, detail.prop);
      // In multi edit mode, we extract the changes which are nested in the data.
    } else if (detail.models) {
      Object.keys(detail.data).forEach((row) => {
        const model = detail.models[row];
        Object.keys(detail.data[row]).forEach((prop) => {
          const val = detail.data[row][prop];
          changes.push(this.getPriceChange(model, prop, val));
          this.invalidateCellCache(model, prop);
        });
      });
    }
    this.$emit('price-changed', changes);
  }

  getPriceChange(model, prop, value) {
    return {
      priceGuid: model[`${prop}_guid`],
      newVal: value
    };
  }

  onBeforeEditStart(event) {
    // To disable the edition of disabled cells.
    if (!this.isEnabledCell(event.detail.prop, this.today)) {
      event.preventDefault();
    }
  }

  onBeforeEdit(event) {
    // This will not save the data if its invalid.
    if (this.validator(event.detail.val) !== true) {
      event.preventDefault();
    } else {
      event.detail.val = Number.parseFloat(event.detail.val);
      this.undoPush(cloneDeep({ rows: this.rows, columns: this.columns, eventArgs: [1] }));
    }
  }

  onBeforeRangeEdit(event) {
    // Bulk edit mode, we need to filter out the changes that will otherwise change disabled cells.
    // For that, we delete the changes and if there is no change left, we prevent the event.
    const { detail } = event;
    const keysToDelete = [];
    Object.keys(detail.data).forEach((row) => {
      Object.keys(detail.data[row]).forEach((prop) => {
        if (!this.isEnabledCell(prop, this.today)) {
          keysToDelete.push({ row, prop });
        }
      });
    });
    keysToDelete.forEach((deleteAble) => {
      delete detail.data[deleteAble.row][deleteAble.prop];
      if (Object.keys(detail.data[deleteAble.row]).length === 0) {
        delete detail.data[deleteAble.row];
      }
    });
    if (Object.keys(detail.data).length === 0) {
      event.preventDefault();
    } else {
      let numberOfChanges = 0;
      Object.keys(detail.data).forEach((row) => {
        Object.keys(detail.data[row]).forEach(() => {
          numberOfChanges += 1;
        });
      });
      this.undoPush(cloneDeep({
        rows: this.rows,
        columns: this.columns,
        eventArgs: [numberOfChanges]
      }));
    }
  }

  onHeaderClick({ detail }) {
    const columnIndex = this.columns.filter((col) => col.children).flatMap((col) => col.children.map((child) => child.prop)).findIndex((prop) => prop === detail.prop);
    const rows = this.rows.length - 1;
    this.rangeSelector?.selectionStoreService?.focus({
      x: columnIndex,
      y: 0
    }, detail.originalEvent.shiftKey);
    this.rangeSelector?.selectionStoreService?.focus({ x: columnIndex, y: rows }, true);
  }

  onBeforeAutoFill(e) {
    e.preventDefault();
  }

  async created() {
    await this.undoClear();
    window.addEventListener('keydown', this.onKeyDown, true);
    window.addEventListener('keyup', this.onKeyUp, true);
  }

  destroyed() {
    window.removeEventListener('keydown', this.onKeyDown, true);
    window.removeEventListener('keyup', this.onKeyUp, true);
  }

  onKeyUp(e) {
    if (e.key === 'z' && e.ctrlKey) {
      e.preventDefault();
      e.stopImmediatePropagation();
      e.stopPropagation();
    }
  }

  onKeyDown(e) {
    if (e.key === 'z' && e.ctrlKey) {
      e.preventDefault();
      e.stopImmediatePropagation();
      e.stopPropagation();
      this.undo();
    }
  }

  async undo() {
    const oldTable = await this.undoPop();
    if (oldTable) {
      this.columns = oldTable.columns;
      this.rows = oldTable.rows;
      this.stylesCache.clear();
      this.$emit('undo', ...oldTable.eventArgs);
    }
  }

  @Watch('frontPrices', { immediate: true })
  onFrontPrices() {
    this.stylesCache.clear();
  }
}

</script>

<style lang="scss">
/*This hacks are needed to disable the default lines, which messes with performance and is harder to
extend.*/
.price-table .colPinStart {
  box-shadow: 0 0 0 #ffffff inset !important;
}

.price-table .rgRow {
  box-shadow: 0 0 0 #ffffff inset !important;
}

.price-table .header-rgRow {
  box-shadow: 0 0 0 #ffffff inset !important;
  text-align: center;
}

.price-table revogr-temp-range.temp-bg-range.Selection {
  border: 2px dashed gray;
  display: block;
}

.price-table revogr-overlay-selection .focused-cell {
  box-shadow: 0 0 0 #ffffff inset !important;
}

.price-table revogr-overlay-selection .selection-border-range {
  box-shadow: 0 0 0 #ffffff inset !important;
  border: #0d63e8 solid 2px;
}
</style>
